import { useCallback } from "react";
import { R_START } from "../lib/timeline/geometry.js";
import { resolutionMomentLocal } from "../lib/formatDates.js";

/**
 * Timeline viewport controller — the zoom/pan animation over the shared
 * ppd/viewLeft refs. Originally a verbatim extraction from AppInner; `centerToday` has
 * since gained one behavior of its own — a resolved forecast centres on its LIFESPAN
 * rather than on today→horizon (`resolvedFrame` below). `jumpTo` and `autoZoom` are
 * still the extracted bodies.
 *
 * @param {object} deps
 * @param {{w:number}} deps.cSize            container size (width read for layout math)
 * @param {{current:{w:number}}} deps.cSizeRef  live container-size mirror (autoZoom reads current width)
 * @param {{current:number}} deps.vlRef      viewLeft ref (day-space left edge; mutated by the animations)
 * @param {{current:number}} deps.ppdRef     pixels-per-day ref (zoom level; mutated by the animations)
 * @param {{current:number|null}} deps.animRef  requestAnimationFrame handle (cancelled/reset per animation)
 * @param {Function} deps.scheduleRender     triggers a timeline re-render after a ref mutation
 * @param {object|null} deps.active          active forecast (centerToday reads its resolution_date)
 * @param {boolean} deps.chatOpen            right panel open (subtracted from usable width)
 * @param {number}  [deps.chatWidth]         right panel's width when open (user-resizable; 264)
 * @param {number}  [deps.logWidth]          left panel's width when open (272, or 330 with tracking)
 * @param {number}  [deps.resolutionRightBuffer] px to keep free right of the forecast line
 *   for whatever it renders (the % label, or the wider distribution marker). Default 110.
 * @param {number}  [deps.labelPadScale] multiplier on that reserved block (1 = as measured);
 *   the navigation-rail layout passes 1.2 for more room right of the bin labels
 * @param {{target:string,startDate:string|null}|null} [deps.resolvedFrame] the LIFESPAN frame
 *   for a resolved active forecast (`lib/forecast/resolvedFrame.js`); null while live
 * @param {boolean} deps.logOpen             left panel open (subtracted from usable width)
 * @param {number} deps.todayDay             today in day-space (R_START offset)
 * @param {Function} deps.setPromotedIds     reset promoted-event set on a manual zoom
 * @param {Function} deps.setCollapsedIds    reset collapsed-event set on a manual zoom
 * @returns {{ jumpTo: Function, centerToday: Function, autoZoom: Function }}
 */
export function useTimelineZoom(deps) {
  const {
    cSize,
    cSizeRef,
    vlRef,
    ppdRef,
    animRef,
    scheduleRender,
    active,
    chatOpen,
    chatWidth = 264,
    logWidth = 272,
    logOpen,
    todayDay,
    resolutionRightBuffer,
    labelPadScale = 1,
    resolvedFrame,
    setPromotedIds,
    setCollapsedIds,
  } = deps;
  const jumpTo = useCallback(
    (targetPpd) => {
      setPromotedIds(new Set());
      setCollapsedIds(new Set());
      const vw = cSize.w || 800;
      const centerDay = vlRef.current + vw / (2 * ppdRef.current);
      if (animRef.current) cancelAnimationFrame(animRef.current);
      const animate = () => {
        const cur = ppdRef.current,
          diff = targetPpd - cur;
        if (Math.abs(diff / targetPpd) < 0.002) {
          ppdRef.current = targetPpd;
          vlRef.current = centerDay - vw / (2 * targetPpd);
          scheduleRender();
          animRef.current = null;
          return;
        }
        const np = cur + diff * 0.15;
        ppdRef.current = np;
        vlRef.current = centerDay - vw / (2 * np);
        scheduleRender();
        animRef.current = requestAnimationFrame(animate);
      };
      animRef.current = requestAnimationFrame(animate);
      // Omitted deps (setPromotedIds/setCollapsedIds setters, vlRef/ppdRef/animRef) are
      // stable bindings passed from AppInner; the hook boundary loses eslint's inference.
    },
    [cSize.w, scheduleRender], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const autoZoom = useCallback(
    (rd, animate = true, startDate = null, bufferOverride = null, keepTodayInFrame = false) => {
      // Live width via ref — not the closed-over cSize.w, which can be the 800
      // placeholder in early-captured closures (e.g. the onboarding tour pipeline).
      const w = cSizeRef.current.w || 800;
      // Subtract panel widths if open so the resolution icon isn't hidden behind them.
      // The panels' ACTUAL widths: the chat is user-resizable, and the log is 330 (not
      // 272) whenever Forecast Tracking is on — the hardcoded 272 framed too wide there.
      const rightPanel = chatOpen ? chatWidth : 0;
      const leftPanel = logOpen ? logWidth : 0;
      // Reserve room to the right of the resolution line for whatever it renders — the
      // big % label, or the wider distribution marker. The caller measures it (it knows
      // which is drawn and how wide the bars are); 110 is the historical % -label value and
      // the fallback when nothing is passed.
      // `bufferOverride` lets a caller reserve space for the forecast IT is framing rather
      // than the one currently active. Boot and forecast-selection both frame a forecast
      // before it lands in `active`, so the closed-over value describes the wrong contract
      // (or, cold, the binary default) and the frame comes out tighter than CENTER's.
      const labelBuffer = (bufferOverride ?? resolutionRightBuffer ?? 110) * labelPadScale;
      const usableW = Math.max(200, w - rightPanel - leftPanel - labelBuffer);
      // Frame to the resolution LINE moment (resolutionMomentLocal: end-of-day for a
      // date-only resolution), matching where resScreenX draws the line — so a
      // resolution of *today* yields a positive span (end-of-today is ahead of "now")
      // instead of collapsing to ~0 and bailing below. (Zoom framing ignores the
      // "before <date>" start-of-day nuance — a sub-day difference is invisible at
      // frame scale — so no question is threaded here.)
      const resMoment = resolutionMomentLocal(rd);
      const resDays = resMoment ? (resMoment.getTime() - R_START) / 86400000 : NaN;
      if (isNaN(resDays)) return;
      // Anchor the LEFT of the frame at the forecast's start (createdAt) when it's
      // older than today, so the GENERATED line + the elapsed span stay in view —
      // the whole forecast lifespan (GENERATED → TODAY → FORECAST). Without this,
      // autoZoom framed from ~today and an older createdAt fell off the left edge,
      // hiding the GENERATED marker. Fresh forecasts (createdAt ≈ today, or no
      // startDate) frame from today exactly as before.
      const startMs = startDate != null ? new Date(startDate).getTime() : NaN;
      // A LIVE forecast whose resolution is still ahead frames today →
      // resolution (the span the reader acts on); its history stays a pan away. A resolved or
      // overdue one keeps the lifespan frame below.
      // A live forecast also shows some of its past — back to when it was made, up to
      // 35% of the span still ahead — so the start of the story stays in view.
      const createdDay = !isNaN(startMs) ? (startMs - R_START) / 86400000 : todayDay;
      const startDay =
        keepTodayInFrame && resDays > todayDay
          ? Math.min(todayDay, Math.max(createdDay, todayDay - (resDays - todayDay) * 0.35))
          : !isNaN(startMs)
            ? Math.min(todayDay, createdDay)
            : todayDay;
      // A LIVE forecast's frame must CONTAIN today. Everything anchored to the today line
      // — the TODAY rule, its label, and the ↻ REFORECAST pill — renders only while
      // `todayX` is on screen (App.jsx), so a frame whose right edge is the resolution date
      // drops that whole cluster the moment today runs past it, and the pill the user needs
      // precisely then (an unresolved forecast past its date) is the first thing to go.
      // Anchoring the left edge at createdAt made that reachable: the span became
      // creation→resolution while today sat beyond its right edge.
      //
      // Opt-in, not unconditional: a RESOLVED forecast frames its lifespan on purpose, and
      // one that settled months ago would squash that history into a sliver if the frame
      // were stretched to now. Callers that frame a live row pass true.
      const endDay = keepTodayInFrame ? Math.max(resDays, todayDay) : resDays;
      const rawSpan = endDay - startDay;
      if (rawSpan <= 0) return;
      // Floor the span so a same-day (sub-day) horizon frames like a ~1-day forecast
      // instead of slamming to the max zoom and rendering the two lines a pixel apart.
      const span = Math.max(rawSpan, 1);
      // The frame: 20% of the usable width left of the forecast's start, 75% for the span,
      // and 5% of breathing room past the resolution line before the reserved label block
      // and the console. That tail was 10% (a 0.7 span) — halved to hand the forecast the
      // width instead of leaving it empty beside the bin labels.
      // 8% margin either side of the framed span (was 20% left / 5% right).
      // Wider margins — a little past on the left, room before the distribution on the right.
      const FRAME_MARGIN = 0.14;
      const FRAME_MARGIN_RIGHT = 0.12;
      const newPpd = Math.max(
        0.08,
        Math.min(1000, (usableW * (1 - FRAME_MARGIN - FRAME_MARGIN_RIGHT)) / span),
      );
      // DEV-only framing trace. Every zoom path funnels through here, so this records what
      // each one actually computed — the width it measured, the buffer it reserved, the span
      // it framed and the ppd it produced. `import.meta.env.DEV` is statically false in every
      // build, so this whole block is dead-code-eliminated and cannot ship.
      //
      // It exists because "the reload zoom is wrong" was diagnosed three times from reading
      // call sites, and three times the wrong call site was fixed. Numbers settle it.
      if (import.meta.env.DEV && typeof window !== "undefined") {
        window.__soothZoom = { w, rightPanel, leftPanel, labelBuffer, usableW, span, newPpd };
      }
      const leftOffset = leftPanel + usableW * FRAME_MARGIN;
      const newVL = startDay - leftOffset / newPpd;
      // animate=false: snap to target without re-running the zoom animation. Used by the
      // post-preprocess refinement so the timeline doesn't visibly re-scale once the user
      // is already looking at the preprocess panel.
      if (!animate) {
        if (animRef.current) {
          cancelAnimationFrame(animRef.current);
          animRef.current = null;
        }
        ppdRef.current = newPpd;
        vlRef.current = newVL;
        scheduleRender();
        return;
      }
      // Animate ppd + viewLeft to their targets over ~600ms instead of jumping.
      // Cancels any pre-existing zoom animation so a rapid resubmit doesn't fight itself.
      if (animRef.current) cancelAnimationFrame(animRef.current);
      const startPpd = ppdRef.current,
        startVL = vlRef.current;
      // Two interpolation tricks so a huge zoom-ratio change (e.g.
      // 1-day forecast → 2-year forecast, where ppd swings ~700x) looks
      // smooth instead of "rapid and broken":
      //
      // 1. ppd interpolates LOG-LINEARLY. Linear-in-pixels means the
      //    visual zoom rate is wildly uneven across the animation — at
      //    the linear midpoint you've already covered most of the
      //    perceptual zoom from the wide end. Lerping log(ppd) instead
      //    makes the perceived zoom rate constant.
      //
      // 2. The today-line PIXEL POSITION interpolates linearly across
      //    the screen. Without this, lerping viewLeft directly while
      //    ppd is changing puts the today line tens of thousands of
      //    pixels off-screen at mid-animation (vl * ppd = nonsense).
      //    Instead, we compute where today should sit at start and end
      //    in screen-x, lerp that, and DERIVE viewLeft from the current
      //    ppd + target today-x. The today line slides smoothly while
      //    the timeline zooms around it.
      const startLogPpd = Math.log(startPpd);
      const targetLogPpd = Math.log(newPpd);
      const startTodayX = (todayDay - startVL) * startPpd;
      const newTodayX = (todayDay - newVL) * newPpd;
      const dur = 600;
      const t0 = performance.now();
      const ease = (t) => 1 - Math.pow(1 - t, 3); // easeOutCubic
      const tick = () => {
        const t = Math.min(1, (performance.now() - t0) / dur);
        const e = ease(t);
        const ppdAtFrame = Math.exp(startLogPpd + (targetLogPpd - startLogPpd) * e);
        const todayXAtFrame = startTodayX + (newTodayX - startTodayX) * e;
        ppdRef.current = ppdAtFrame;
        vlRef.current = todayDay - todayXAtFrame / ppdAtFrame;
        scheduleRender();
        if (t < 1) animRef.current = requestAnimationFrame(tick);
        else animRef.current = null;
      };
      animRef.current = requestAnimationFrame(tick);
      // The omitted deps are the stable refs cSizeRef/vlRef/ppdRef/animRef, passed from
      // AppInner — the hook boundary loses eslint's ref-stability inference. (cSize.w,
      // scheduleRender, todayDay, chatOpen, logOpen, and the panel widths are the reactive
      // deps and ARE listed.)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      cSize.w,
      scheduleRender,
      todayDay,
      chatOpen,
      logOpen,
      resolutionRightBuffer,
      labelPadScale,
      chatWidth,
      logWidth,
    ],
  );

  // Declared after autoZoom (which it calls) to avoid a forward reference. Moved here
  // from before autoZoom in the original source — the only reorder. jumpTo/autoZoom don't
  // reference centerToday, so ordering is neutral. The body is no longer the extracted one:
  // it branches on `resolvedFrame` (see the resolved-forecast note in the header).
  const centerToday = useCallback(() => {
    // Match autoZoom layout: today at 20% of usable width, resolution at ~90%
    if (!active?.contract?.resolution_date) {
      vlRef.current = todayDay - ((cSize.w || 800) * 0.47) / ppdRef.current;
      scheduleRender();
      return;
    }
    // A resolved forecast centres on its LIFESPAN, not on today→horizon — the same frame
    // the on-selection effect uses. CENTER is the button reached for when the framing is
    // wrong, so leaving it on the live rule would hand back the zoom being escaped from.
    if (resolvedFrame) {
      autoZoom(resolvedFrame.target, true, resolvedFrame.startDate);
      scheduleRender();
      return;
    }
    // Anchor the left edge at the forecast's CREATION, not today, so a long-running
    // forecast frames its whole lifespan (GENERATED -> TODAY -> FORECAST) and its
    // reforecast history stays on screen. Framing from today pushed every vintage
    // older than today off the left edge, so opening an old forecast showed a bare
    // flat line and the history only reappeared if the user zoomed out far enough.
    // autoZoom clamps via Math.min(todayDay, startMs), so a fresh forecast
    // (createdAt ~ today) frames exactly as it did before. Changed in lockstep with
    // BOTH selection call sites in App.jsx so selecting and centering still land on
    // the same zoom -- the consistency the previous no-startDate rule was protecting.
    // keepTodayInFrame: this branch is the LIVE one (the resolved frame returned above),
    // so the frame must contain today — otherwise a forecast past its resolution date
    // frames creation→resolution with today off the right edge, taking the TODAY rule
    // and the ↻ REFORECAST pill anchored to it off screen with it.
    autoZoom(active.contract.resolution_date, true, active?.createdAt ?? null, null, true);
    scheduleRender();
    // Omitted: vlRef/ppdRef (stable refs), plus autoZoom and todayDay — both read here but
    // deliberately unlisted, carried over from the original AppInner array
    // [active,cSize.w,scheduleRender,chatOpen,logOpen].
    //
    // resolutionRightBuffer and resolvedFrame are the two ADDITIONS to that array, so this
    // is no longer a verbatim port. resolvedFrame is read directly in the body above. It is load-bearing rather than cosmetic: autoZoom closes over the
    // buffer, and since autoZoom itself is unlisted, this is what re-creates centerToday
    // (and with it a fresh autoZoom) when the buffer changes — without it, centering keeps
    // an autoZoom that reserves the wrong right-hand gap and tucks the winning row under
    // the chat console.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    active,
    cSize.w,
    scheduleRender,
    chatOpen,
    logOpen,
    resolutionRightBuffer,
    labelPadScale,
    resolvedFrame,
    // Same reason as the buffer: a resized chat must re-create the autoZoom this uses.
    chatWidth,
    logWidth,
  ]);
  return { jumpTo, centerToday, autoZoom };
}
